import { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authApi } from '@/features/auth/api/auth.api';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { GUIDE_ENTRY_ROUTE } from '@/features/auth/constants';
import { AuthModal } from '@/features/auth/components/AuthModal';

const NAVBAR_HEIGHT = 72;

/**
 * Routes whose first section is a full-bleed hero that the navbar sits on top
 * of. On these the bar starts transparent with light-on-dark contents and only
 * takes on its solid surface once scrolled past the hero. Every other route
 * gets the solid bar from the start, because there is nothing behind it.
 */
const OVERLAY_HERO_ROUTES = ['/', GUIDE_ENTRY_ROUTE];

interface NavbarProps {
  /**
   * Overrides the route-based default. A route in `OVERLAY_HERO_ROUTES` can
   * still render states without a hero behind the bar — `/become-guide` after
   * the application is submitted, for one — and light-on-dark contents would
   * be invisible there.
   */
  overlayHero?: boolean;
}

export function Navbar({ overlayHero }: NavbarProps = {}) {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const hasOverlayHero = overlayHero ?? OVERLAY_HERO_ROUTES.includes(location.pathname);
  const isTransparent = hasOverlayHero && !isScrolled;

  useEffect(() => {
    if (!hasOverlayHero) {
      setIsScrolled(false);
      return;
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasOverlayHero]);

  const handleLogout = async () => {
    setAnchorEl(null);
    try {
      await authApi.logout();
    } finally {
      logout();
      navigate('/', { replace: true });
    }
  };

  const handleLoginSuccess = (route: string) => {
    setLoginModalOpen(false);
    navigate(route, { replace: true });
  };

  const navLinks = [
    { label: 'How It Works', to: '/how-it-works' },
    { label: 'Become a Guide', to: GUIDE_ENTRY_ROUTE },
    { label: 'Blogs', to: '/blogs' },
    { label: 'About Us', to: '/about' },
  ];

  return (
    <>
      <AppBar
        position={hasOverlayHero ? 'fixed' : 'sticky'}
        elevation={0}
        sx={{
          top: 0,
          zIndex: 1100,
          bgcolor: isTransparent ? 'transparent' : 'background.paper',
          backgroundImage: 'none',
          borderBottom: isTransparent ? 'none' : '1px solid',
          borderColor: isTransparent ? 'transparent' : 'divider',
          boxShadow: isTransparent ? 'none' : '0 1px 3px rgba(15, 23, 42, 0.05)',
          transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 6, sm: 6, md: 6 } }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              minHeight: { xs: 64, md: NAVBAR_HEIGHT },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Logo */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                zIndex: 2,
                flexShrink: 0,
                py: 0.5,
                transition: 'transform 200ms ease',
                '&:hover': { transform: 'scale(1.02)' },
              }}
            >
              <BrandLogo
                size="xl"
                surface={isTransparent ? 'dark' : 'light'}
                responsiveHeight={{ xs: 44, sm: 50, md: 56 }}
              />
            </Box>

            {/* Center nav — absolutely centered like reference */}
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                alignItems: 'center',
                gap: 2,
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              {navLinks.map((link) => (
                <Button
                  key={link.label}
                  component={RouterLink}
                  to={link.to}
                  sx={{
                    color: isTransparent ? 'rgba(255, 255, 255, 0.92)' : 'text.secondary',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    px: 2.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: isTransparent ? 'common.white' : 'text.primary',
                      bgcolor: isTransparent ? 'rgba(255, 255, 255, 0.1)' : 'action.hover',
                    },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>

            {/* Right actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 3, md: 3 }, zIndex: 2, flexShrink: 0 }}>
              {isAuthenticated && user ? (
                <>
                  <Button
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: { xs: 18, md: 24 } }} />}
                    sx={{
                      color: isTransparent ? 'common.white' : 'text.primary',
                      textTransform: 'none',
                      fontWeight: 500,
                      minWidth: 0,
                      px: { xs: 0.5, md: 1.5 },
                      '& .MuiButton-endIcon': {
                        ml: { xs: 0.25, md: 0.75 },
                      },
                      '&:hover': {
                        bgcolor: isTransparent ? 'rgba(255, 255, 255, 0.1)' : 'action.hover',
                      },
                    }}
                  >
                    <Avatar
                      src={user.avatar}
                      sx={{
                        width: 32,
                        height: 32,
                        mr: { xs: 0, sm: 1 },
                        bgcolor: 'primary.main',
                        fontSize: '0.875rem',
                      }}
                    >
                      {(user.firstName?.[0] ?? user.phone?.[0] ?? 'U').toUpperCase()}
                    </Avatar>
                    <Box
                      component="span"
                      sx={{ display: { xs: 'none', sm: 'inline' } }}
                    >
                      {user.firstName ?? user.displayName ?? 'Account'}
                    </Box>
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        borderRadius: 2,
                        boxShadow: '0 10px 40px rgba(15, 23, 42, 0.15)',
                      },
                    }}
                  >
                    <MenuItem
                      component={RouterLink}
                      to="/my-bookings"
                      onClick={() => setAnchorEl(null)}
                    >
                      My Bookings
                    </MenuItem>
                    <MenuItem
                      component={RouterLink}
                      to="/profile"
                      onClick={() => setAnchorEl(null)}
                    >
                      Profile
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>Sign Out</MenuItem>
                  </Menu>
                </>
              ) : (
                <Button
                  onClick={() => setLoginModalOpen(true)}
                  variant={isTransparent ? 'outlined' : 'contained'}
                  sx={
                    isTransparent
                      ? {
                          color: 'white',
                          borderColor: 'rgba(255, 255, 255, 0.75)',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          px: 2.5,
                          py: 0.875,
                          borderRadius: 2,
                          textTransform: 'none',
                          bgcolor: 'transparent',
                          '&:hover': {
                            borderColor: 'white',
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }
                      : {
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          px: 2.5,
                          py: 1,
                          borderRadius: 2,
                          textTransform: 'none',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                          },
                        }
                  }
                >
                  Sign In
                </Button>
              )}

              {/* Mobile Menu Button */}
              <IconButton
                sx={{
                  display: { xs: 'flex', lg: 'none' },
                  color: isTransparent ? 'common.white' : 'text.primary',
                  p: { xs: 0.75, md: 1 },
                }}
                onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </AppBar>

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={() => setMobileMenuAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(15, 23, 42, 0.15)',
          },
        }}
      >
        {navLinks.map((link) => (
          <MenuItem
            key={link.label}
            component={RouterLink}
            to={link.to}
            onClick={() => setMobileMenuAnchor(null)}
          >
            {link.label}
          </MenuItem>
        ))}
        {!isAuthenticated && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                setMobileMenuAnchor(null);
                setLoginModalOpen(true);
              }}
            >
              Sign In
            </MenuItem>
          </>
        )}
      </Menu>

      <AuthModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onAuthSuccess={handleLoginSuccess}
        travelerSearchFlow
        title="Sign in"
        subtitle="Create a free account or sign in to explore guides and manage your bookings."
      />
    </>
  );
}
